import { useRef, useEffect, useState } from 'react'
import { Table } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { X, Filter, ChevronDown, CheckIcon } from 'lucide-react'
import { format } from 'date-fns'
import type { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { cn } from '@/lib/utils'
import { debounceAsync } from '@/utils/debounce'
import { DataTableFacetedFilter } from '@/views/users/components/data-table-faceted-filter'
import { DataTableViewOptions } from '@/components/data-table/data-table-view-options'
import { DateRangePicker } from '@/components/date-range-picker'
import type { HonorairesTableParams } from '../Honoraires'

interface HonorairesToolbarProps<TData> {
  table: Table<TData>
  params: HonorairesTableParams
  setParams: React.Dispatch<React.SetStateAction<HonorairesTableParams>>
}

// Predefined amount ranges: { label, value, min, max }
const getAmountRanges = (t: any, lang: string) => {
  const currency = lang === 'ar' ? 'درهم' : 'MAD'
  return [
    { label: `< 1 000 ${currency}`, value: 'lt_1000', min: undefined as number | undefined, max: 1000 },
    { label: `1 000 – 5 000 ${currency}`, value: '1k_5k', min: 1000, max: 5000 },
    { label: `5 000 – 10 000 ${currency}`, value: '5k_10k', min: 5000, max: 10000 },
    { label: `10 000 – 50 000 ${currency}`, value: '10k_50k', min: 10000, max: 50000 },
    { label: `> 50 000 ${currency}`, value: 'gt_50000', min: 50000, max: undefined as number | undefined },
    { label: t('custom_range') || 'Personnalisé', value: 'custom', min: undefined as number | undefined, max: undefined as number | undefined },
  ]
}

const STATUS_OPTIONS = [
  { label: 'honoraires_status_paid', value: 'paid' },
  { label: 'honoraires_status_partial', value: 'partial' },
  { label: 'honoraires_status_unpaid', value: 'unpaid' },
  { label: 'honoraires_status_pending', value: 'pending' },
]

function AmountRangeFilter({
  params,
  setParams,
}: {
  params: HonorairesTableParams
  setParams: React.Dispatch<React.SetStateAction<HonorairesTableParams>>
}) {
  const { t, i18n } = useTranslation()
  const AMOUNT_RANGES = getAmountRanges(t, i18n.language)

  const [open, setOpen] = useState(false)
  const [customMin, setCustomMin] = useState(params.min_amount?.toString() ?? '')
  const [customMax, setCustomMax] = useState(params.max_amount?.toString() ?? '')

  // Detect active predefined range from current params
  const detectSelected = () => {
    const { min_amount, max_amount } = params
    if (min_amount == null && max_amount == null) return null
    for (const r of AMOUNT_RANGES.filter(r => r.value !== 'custom')) {
      if (r.min === min_amount && r.max === max_amount) return r.value
    }
    return 'custom'
  }

  const [selected, setSelected] = useState<string | null>(detectSelected)

  // Keep `selected` in sync when params reset externally
  useEffect(() => {
    setSelected(detectSelected())
    setCustomMin(params.min_amount?.toString() ?? '')
    setCustomMax(params.max_amount?.toString() ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.min_amount, params.max_amount])

  const handleSelect = (value: string) => {
    if (value === selected) {
      // Deselect
      setSelected(null)
      setParams(p => ({ ...p, min_amount: undefined, max_amount: undefined }))
      if (value !== 'custom') setOpen(false)
      return
    }
    const range = AMOUNT_RANGES.find(r => r.value === value)!
    setSelected(value)
    if (value !== 'custom') {
      setParams(p => ({ ...p, min_amount: range.min, max_amount: range.max }))
      setOpen(false)
    }
  }

  const applyCustom = () => {
    const min = customMin ? parseFloat(customMin) : undefined
    const max = customMax ? parseFloat(customMax) : undefined
    setParams(p => ({ ...p, min_amount: min, max_amount: max }))
    setOpen(false)
  }

  const hasValue = selected !== null

  const displayLabel = () => {
    if (!hasValue) return t('amount') || 'Montant'
    if (selected === 'custom') {
      const parts = [
        customMin ? `≥ ${customMin}` : null,
        customMax ? `≤ ${customMax}` : null,
      ].filter(Boolean)
      return parts.join(' — ')
    }
    return AMOUNT_RANGES.find(r => r.value === selected)?.label ?? ''
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 border-dashed text-sm">
          <Filter className="me-2 h-4 w-4" />
          {t('amount') || 'Montant'}
          {hasValue && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1.5 font-normal">
                <span dir={selected !== 'custom' ? 'ltr' : undefined}>{displayLabel()}</span>
              </Badge>
            </>
          )}
          <ChevronDown className="ml-2 h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="start">
        <Command>
          <CommandList>
            <CommandEmpty>{t('no_results')}</CommandEmpty>
            <CommandGroup>
              {AMOUNT_RANGES.filter(r => r.value !== 'custom').map((range) => {
                const isSelected = selected === range.value
                return (
                  <CommandItem key={range.value} onSelect={() => handleSelect(range.value)}>
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                      )}
                    >
                      <CheckIcon className="h-3 w-3" />
                    </div>
                    <span dir="ltr" className="flex-1 text-left">{range.label}</span>
                  </CommandItem>
                )
              })}

              {/* Custom range */}
              <CommandItem onSelect={() => handleSelect('custom')}>
                <div
                  className={cn(
                    'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                    selected === 'custom' ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible',
                  )}
                >
                  <CheckIcon className="h-3 w-3" />
                </div>
                {t('custom_range') || 'Personnalisé'}
              </CommandItem>
            </CommandGroup>


            {selected === 'custom' && (
              <>
                <Separator />
                <div className="p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      placeholder={t('min_amount') || 'Min'}
                      value={customMin}
                      onChange={(e) => setCustomMin(e.target.value)}
                      className="h-8 text-sm"
                    />
                    <span className="text-muted-foreground text-xs">–</span>
                    <Input
                      type="number"
                      placeholder={t('max_amount') || 'Max'}
                      value={customMax}
                      onChange={(e) => setCustomMax(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <Button size="sm" className="w-full h-8" onClick={applyCustom}>
                    {t('apply') || 'Appliquer'}
                  </Button>
                </div>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export function HonorairesToolbar<TData>({
  table,
  params,
  setParams,
}: HonorairesToolbarProps<TData>) {
  const { t } = useTranslation()

  const debouncedSearch = useRef(
    debounceAsync(async (value: string) => {
      setParams(p => ({ ...p, search: value === '' ? '' : value, page: 0 }))
    }, 400),
  ).current

  const hasFilters =
    !!params.search ||
    !!params.status ||
    params.min_amount != null ||
    params.max_amount != null ||
    !!params.date_min ||
    !!params.date_max

  const statusOptions = STATUS_OPTIONS.map(o => ({ label: t(o.label), value: o.value }))

  const handleStatusChange = (values: string[]) => {
    setParams(p => ({ ...p, status: values.length > 0 ? values[0] : undefined, page: 0 }))
  }

  const handleDateChange = (dateRange: DateRange | undefined) => {
    setParams(p => ({
      ...p,
      date_min: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
      date_max: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      page: 0,
    }))
  }

  const currentDateRange: DateRange | undefined =
    params.date_min || params.date_max
      ? {
        from: params.date_min ? new Date(params.date_min) : undefined,
        to: params.date_max ? new Date(params.date_max) : undefined,
      }
      : undefined

  const resetAll = () => {
    setParams(p => ({
      ...p,
      search: '',
      status: undefined,
      min_amount: undefined,
      max_amount: undefined,
      date_min: undefined,
      date_max: undefined,
      page: 0,
    }))
  }

  return (
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {/* Search */}
        <Input
          placeholder={t('honoraires_search')}
          defaultValue={params.search ?? ''}
          onChange={(e) => debouncedSearch(e.target.value)}
          className="h-9 w-full sm:max-w-[220px]"
        />

        {/* Status Faceted Filter (single select) */}
        <DataTableFacetedFilter
          title={t('honoraires_col_status')}
          options={statusOptions}
          defaultSelected={params.status ? [params.status] : []}
          onFacetChange={handleStatusChange}
        />

        {/* Amount Range Smart Filter */}
        <AmountRangeFilter params={params} setParams={setParams} />

        {/* Date Range */}
        <DateRangePicker
          date={currentDateRange}
          onDateChange={handleDateChange}
          className="h-9 w-full sm:w-[260px]"
          placeholder={t('filter_by_date') || 'Période'}
        />

        {/* Reset */}
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetAll} className="h-9 px-2 text-muted-foreground hover:text-foreground">
            {t('reset_filters') || 'Réinitialiser'}
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}
      </div>

      <DataTableViewOptions table={table} />
    </div>
  )
}
